import { motion, useInView } from 'motion/react';
import { useRef, useState } from 'react';
import { Quote, TrendingUp, Clock, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';

export function SuccessStories() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [activeStory, setActiveStory] = useState(0);

  const stories = [
    {
      company: 'TechCommerce Brasil',
      segment: 'E-commerce B2C',
      size: '50-100 funcionários',
      author: 'Marina Costa',
      role: 'Head de Marketing',
      avatar: 'MC',
      quote: 'Estávamos gastando R$ 85k/mês em ads e não sabíamos quanto retornava. Após WPTT, descobrimos que 47% era tráfego de bot. Cortamos o desperdício e aumentamos ROI em 340% mantendo o mesmo budget.',
      metrics: [
        { icon: DollarSign, label: 'Economia', value: 'R$ 40k', period: '/mês', color: '#10B981' },
        { icon: TrendingUp, label: 'ROI', value: '+340%', period: 'em 60 dias', color: '#4ECDC4' },
        { icon: Clock, label: 'Tempo', value: '89h', period: 'economizadas', color: '#FFE66D' },
      ],
      before: 'R$ 85k em ads, ROI desconhecido, 47% bots',
      after: 'R$ 45k em ads reais, ROI de 4.2x, 0% bots',
    },
    {
      company: 'SaaS Growth Lab',
      segment: 'SaaS B2B',
      size: '20-50 funcionários',
      author: 'Ricardo Almeida',
      role: 'CEO & Founder',
      avatar: 'RA',
      quote: 'Tínhamos 5 ferramentas diferentes para rastreamento. Migrar tudo para WPTT nos economizou R$ 12k/mês em assinaturas e deu visibilidade total da jornada do cliente. Conversão aumentou 67%.',
      metrics: [
        { icon: DollarSign, label: 'Economia', value: 'R$ 12k', period: '/mês', color: '#10B981' },
        { icon: TrendingUp, label: 'Conversão', value: '+67%', period: 'em leads', color: '#4ECDC4' },
        { icon: Clock, label: 'Setup', value: '< 8h', period: 'total', color: '#FFE66D' },
      ],
      before: '5 ferramentas, dados fragmentados, conversão 2.3%',
      after: '1 dashboard, visão 360°, conversão 3.84%',
    },
    {
      company: 'AgênciaPro Digital',
      segment: 'Agência de Marketing',
      size: '10-20 funcionários',
      author: 'Juliana Santos',
      role: 'Diretora de Performance',
      avatar: 'JS',
      quote: 'Gerenciamos 30+ clientes. WPTT nos permitiu oferecer relatórios em tempo real com métricas que os clientes realmente entendem. Reduzimos churn em 45% e aumentamos ticket médio em 28%.',
      metrics: [
        { icon: TrendingUp, label: 'Churn', value: '-45%', period: 'redução', color: '#10B981' },
        { icon: DollarSign, label: 'Ticket', value: '+28%', period: 'aumento', color: '#4ECDC4' },
        { icon: Clock, label: 'Relatórios', value: '5min', period: 'vs 2h antes', color: '#FFE66D' },
      ],
      before: 'Relatórios manuais, alta taxa de churn, clientes confusos',
      after: 'Dashboards automáticos, retenção alta, clientes satisfeitos',
    },
  ];

  const currentStory = stories[activeStory];

  const nextStory = () => {
    setActiveStory((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    setActiveStory((prev) => (prev - 1 + stories.length) % stories.length);
  };

  return (
    <section ref={ref} className="relative py-24 bg-gradient-to-b from-[#0A0E1A] to-[#0B0E14] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #4ECDC4 1px, transparent 0)`,
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FFE66D]/30 bg-[#FFE66D]/10 backdrop-blur-sm mb-6">
            <Quote className="w-4 h-4 text-[#FFE66D]" />
            <span className="text-[#FFE66D] font-mono text-sm uppercase tracking-wider">
              Histórias Reais
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Transformações
            <br />
            <span className="bg-gradient-to-r from-[#FFE66D] via-[#FF6B35] to-[#4ECDC4] bg-clip-text text-transparent">
              Documentadas
            </span>
          </h2>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Casos reais de empresas que saíram do <span className="text-[#FF6B35]">caos</span> para o 
            <span className="text-[#4ECDC4]"> controle total</span>
          </p>
        </motion.div>

        {/* Story Card */}
        <motion.div
          key={activeStory}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          <div className="relative p-8 md:p-12 rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-lg">
            {/* Quote Icon */}
            <div className="absolute top-8 right-8 opacity-10">
              <Quote className="w-32 h-32 text-[#FFE66D]" />
            </div>

            {/* Header */}
            <div className="flex flex-col md:flex-row gap-6 mb-8 relative z-10">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#4ECDC4] to-[#10B981] flex items-center justify-center text-white text-2xl font-bold">
                  {currentStory.avatar}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {currentStory.company}
                </h3>
                <div className="flex flex-wrap gap-3 mb-3">
                  <span className="px-3 py-1 rounded-full bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 text-[#4ECDC4] text-sm">
                    {currentStory.segment}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm">
                    {currentStory.size}
                  </span>
                </div>
                <div className="text-gray-400">
                  <span className="font-semibold text-white">{currentStory.author}</span>
                  {' · '}
                  <span>{currentStory.role}</span>
                </div>
              </div>
            </div>

            {/* Quote */}
            <blockquote className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-8 relative z-10">
              "{currentStory.quote}"
            </blockquote>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {currentStory.metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${metric.color}20 0%, ${metric.color}05 100%)`,
                        border: `1px solid ${metric.color}30`,
                      }}
                    >
                      <metric.icon className="w-5 h-5" style={{ color: metric.color }} />
                    </div>
                    <div className="text-sm text-gray-400">{metric.label}</div>
                  </div>
                  <div className="text-3xl font-bold mb-1" style={{ color: metric.color }}>
                    {metric.value}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{metric.period}</div>
                </motion.div>
              ))}
            </div>

            {/* Before/After */}
            <div className="grid md:grid-cols-2 gap-6 relative z-10">
              <div className="p-6 rounded-2xl border border-[#FF6B35]/30 bg-[#FF6B35]/5">
                <div className="text-[#FF6B35] font-semibold mb-2 text-sm uppercase tracking-wider">
                  Antes do WPTT
                </div>
                <p className="text-gray-300">{currentStory.before}</p>
              </div>
              <div className="p-6 rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5">
                <div className="text-[#10B981] font-semibold mb-2 text-sm uppercase tracking-wider">
                  Depois do WPTT
                </div>
                <p className="text-gray-300">{currentStory.after}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-6 mt-12">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={prevStory}
            className="w-12 h-12 rounded-full border border-[#4ECDC4]/30 bg-[#4ECDC4]/10 hover:bg-[#4ECDC4]/20 flex items-center justify-center transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-[#4ECDC4]" />
          </motion.button>

          {/* Dots */}
          <div className="flex gap-3">
            {stories.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveStory(index)}
                className={`
                  h-2 rounded-full transition-all duration-300
                  ${index === activeStory 
                    ? 'w-12 bg-gradient-to-r from-[#4ECDC4] to-[#10B981]' 
                    : 'w-2 bg-white/20 hover:bg-white/40'
                  }
                `}
              />
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={nextStory}
            className="w-12 h-12 rounded-full border border-[#4ECDC4]/30 bg-[#4ECDC4]/10 hover:bg-[#4ECDC4]/20 flex items-center justify-center transition-all"
          >
            <ChevronRight className="w-6 h-6 text-[#4ECDC4]" />
          </motion.button>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-2xl text-gray-300 mb-6">
            Pronto para escrever <span className="text-[#FFE66D] font-semibold">sua história</span> de sucesso?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-5 bg-gradient-to-r from-[#FF6B35] to-[#FFE66D] rounded-xl font-bold text-white text-lg shadow-2xl"
          >
            Começar Agora
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
